import { toDateInputValue } from './couponAdapter';
import { getCouponStatus } from './couponStatus';

/**
 * CSV export for the coupon list.
 *
 * Hand-rolled rather than using `react-csv`, which is already a dependency and already used in
 * DirectorCamp.js. Two defects in its 2.2.2 source rule it out here:
 *
 *   1. `joiner` wraps every cell in quotes but never doubles the quotes *inside* a cell, so a
 *      coupon named `Summer "Early Bird"` emits `"Summer "Early Bird""` and every column after it
 *      shifts. Coupon names are free text, so that is reachable, not theoretical.
 *   2. `CSVLink.render` calls `buildURI` on every render and that ends in `URL.createObjectURL`
 *      with no matching revoke. This page re-renders on each keystroke of its client-side search,
 *      so the button alone would retain a blob of the whole export per keystroke.
 *
 * Both are handled below. `DirectorCamp.js` is left alone — different dataset, out of scope.
 */

/**
 * The columns are exactly what `fromApi` produces, which is in turn exactly what the
 * `getCouponsList` projection returns. Three columns the design mocked up — Scope, Programs and
 * Categories — are absent because no endpoint currently returns a coupon's targeting ObjectIds;
 * they arrive with the edit work.
 *
 * "Amount discounted" is deliberately absent too. Multiplying redemptions by face value assumes
 * every redemption took the full amount, which is not true of percentage coupons or of bookings
 * that only partly qualified. The real figure lives on the booking (`couponDiscount`), and a
 * number a director might reconcile against is worse wrong than missing.
 */
const COLUMNS = [
    { header: 'Coupon Name', value: coupon => coupon.name },
    { header: 'Coupon Code', value: coupon => coupon.code },
    // Same derivation the status chips use, so an export can never contradict the screen.
    { header: 'Status', value: coupon => getCouponStatus(coupon) },
    { header: 'Type', value: coupon => coupon.type },
    { header: 'Value', value: coupon => coupon.value },
    { header: 'Redeemed', value: coupon => coupon.redeemed },
    // `toLimit` already folds null, undefined and 0 into null, so null is the only unlimited case.
    { header: 'Usage Limit', value: coupon => coupon.possibleUsage ?? 'Unlimited' },
    { header: 'Per-User Limit', value: coupon => coupon.perUserUsage ?? 'Unlimited' },
    // YYYY-MM-DD rather than the `MMM D, YYYY` shown on screen: spreadsheets sort ISO dates
    // correctly and parse "Jun 1, 2026" differently depending on locale. Empty when unset.
    // Wrapped in `asText` — see the comment there — or Excel reformats the unquoted ISO string as
    // a date in its own display format and truncates it to "####" once that no longer fits the
    // column.
    { header: 'Start Date', value: coupon => asText(toDateInputValue(coupon.startDate)) },
    { header: 'End Date', value: coupon => asText(toDateInputValue(coupon.endDate)) },
    { header: 'Visibility', value: coupon => (coupon.isPrivate ? 'Private' : 'Public') },
    { header: 'Refundable', value: coupon => (coupon.refundable ? 'Yes' : 'No') },
    { header: 'Created', value: coupon => asText(toDateInputValue(coupon.created)) },
];

/**
 * Excel (and Sheets, reading the same file) auto-detects an unquoted `YYYY-MM-DD` value as a date
 * and reformats it in its own display format, which overflows the never-widened column into
 * `####`. Wrapping it as a one-cell formula that evaluates to the string forces it to be read as
 * literal text instead — the standard workaround for this, not specific to this export. Safe from
 * formula injection here because the content inside the quotes is always a date string this file
 * generates itself, never free text a director typed.
 *
 * `escapeCell` below still RFC-4180-quotes the result on top of this, and the two compose
 * correctly: the formula syntax is just more characters inside the cell's raw text.
 */
const asText = value => (value ? `="${value}"` : '');

/** RFC 4180: quote a cell only when it needs it, and double any quotes inside it. */
const escapeCell = value => {
    const text = value === null || value === undefined ? '' : String(value);
    return /["\n,]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildCsv = coupons =>
    [
        COLUMNS.map(column => column.header),
        ...coupons.map(coupon => COLUMNS.map(column => column.value(coupon))),
    ]
        .map(row => row.map(escapeCell).join(','))
        .join('\n');

export const csvFileName = scope =>
    `campsez-coupons-${scope}-${toDateInputValue(Date.now())}.csv`;

export const downloadCsv = (coupons, fileName) => {
    // The BOM is what makes Excel read the file as UTF-8; without it a coupon name with an accent
    // or a curly quote arrives mojibaked. Sheets and every text editor ignore it.
    const blob = new Blob(['﻿', buildCsv(coupons)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // The half `react-csv` forgets. Without it the blob is held for the life of the page.
    URL.revokeObjectURL(url);
};
